/**
 * @swagger
 *  components:
 *    schemas:
 *      SingleDate:
 *        type: object
 *        required: [ "precision", "year" ]
 *        properties:
 *          precision:
 *            type: string
 *            enum: [ "exact_datetime", "exact_date", "approximate_datetime", "approximate_date", "year_month", "year" ]
 *          dateStr:
 *            type: string
 *          isApproximate:
 *            type: boolean
 *          year:
 *            type: number
 *          month:
 *            type: number
 *          day:
 *            type: number
 *          time:
 *            type: string
 *      DatePayload:
 *        type: object
 *        required: [ "type", "from", "formatted" ]
 *        properties:
 *          type:
 *            type: string
 *            enum: [ "date", "range" ]
 *            default: "date"
 *          from:
 *            $ref: '#/components/schemas/SingleDate'
  *          to:
 *            $ref: '#/components/schemas/SingleDate'
 *          formatted:
 *            type: string
 *          duration:
 *            type: string
 *            description: Optional duration string (e.g. "2 hours", "3 days", "1 week")
 */

/**
 * Precision level of a date item.
 */
export type DatePrecision =
  | "exact_datetime"
  | "exact_date"
  | "approximate_datetime"
  | "approximate_date"
  | "year_month"
  | "year";

/**
 * Representation of a single date component within a structured date.
 */
export interface ISingleDate {
  precision: DatePrecision;
  dateStr: string;
  isApproximate?: boolean;
  year: number;
  month?: number;
  day?: number;
  time?: string;
}

/**
 * Context type for the DatePicker and payload: "date" for single date or "range" for span.
 */
export type DateType = "date" | "range";

/**
 * Mode determining if the date is a single point in time or a span/range.
 */
export type DateMode = "single" | "range" | "date";

/**
 * Canonical duration units supported across the platform.
 */
export type DateDurationUnit =
  | "minutes"
  | "hours"
  | "days"
  | "weeks"
  | "months"
  | "years";

/**
 * Structured representation of a date duration.
 */
export interface IDateDuration {
  value: number;
  unit: DateDurationUnit;
  raw?: string;
}

/**
 * Full structured date payload stored and transported as JSON.
 * When type is "date", uses the `from` date.
 * When type is "range", uses `from` and `to` dates (or calculated `to` via `duration`).
 */
export interface IDate {
  type?: DateType;
  from?: ISingleDate;
  to?: ISingleDate;
  formatted: string;
  userDefined?: boolean;
  timezoneOffset?: number;
  duration?: string;
  mode?: DateMode;
  single?: ISingleDate;
}

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const MONTH_ABBREVIATIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Normalizes a time string to "HH:mm" (stripping seconds if present).
 *
 * @param {string | undefined} t - Raw time string.
 * @returns {string} Formatted HH:mm time string.
 */
export function formatTimeHHmm(t?: string): string {
  if (!t) return "00:00";
  const parts = t.trim().split(":");
  if (parts.length >= 2) {
    const hh = parts[0].padStart(2, "0");
    const mm = parts[1].padStart(2, "0");
    return `${hh}:${mm}`;
  }
  return t;
}

/**
 * Determines if a given year is a leap year.
 *
 * @param {number} year - Four-digit year.
 * @returns {boolean} True if leap year.
 */
export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Gets the maximum number of valid days in a given month and year.
 *
 * @param {number} year - Four-digit year.
 * @param {number} month - 1-indexed month (1-12).
 * @returns {number} Days in month (28-31).
 */
export function getDaysInMonth(year: number, month: number): number {
  if (month < 1 || month > 12) return 31;
  if (month === 2) {
    return isLeapYear(year) ? 29 : 28;
  }
  if ([4, 6, 9, 11].includes(month)) {
    return 30;
  }
  return 31;
}

/**
 * Validates whether a time string matches 24-hour "HH:mm" format (00:00 to 23:59).
 *
 * @param {string | undefined} time - Time string to test.
 * @returns {boolean} True if valid 24-hour HH:mm.
 */
export function isValidTimeHHmm(time?: string): boolean {
  if (!time) return false;
  const match = time.trim().match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  return Boolean(match);
}

/**
 * Result structure returned by date validators.
 */
export interface IDateValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates a single date item against canonical rules.
 *
 * @param {ISingleDate | undefined} item - The date item to validate.
 * @returns {IDateValidationResult} Validation outcome with error messages.
 */
export function validateSingleDate(item?: ISingleDate): IDateValidationResult {
  const errors: string[] = [];
  if (!item) {
    return { isValid: false, errors: ["Date item is required"] };
  }

  // Validate year
  if (typeof item.year !== "number" || isNaN(item.year) || item.year < 1 || item.year > 9999) {
    errors.push("Year must be between 1 and 9999");
  }

  const needsMonth = item.precision !== "year";
  if (needsMonth) {
    if (typeof item.month !== "number" || isNaN(item.month) || item.month < 1 || item.month > 12) {
      errors.push("Month must be between 1 and 12");
    }
  }

  const needsDay =
    item.precision === "exact_datetime" ||
    item.precision === "exact_date" ||
    item.precision === "approximate_datetime" ||
    item.precision === "approximate_date";

  if (needsDay) {
    const maxDays = getDaysInMonth(item.year || 2026, item.month || 1);
    if (typeof item.day !== "number" || isNaN(item.day) || item.day < 1 || item.day > maxDays) {
      const monthName = item.month && item.month >= 1 && item.month <= 12 ? MONTH_ABBREVIATIONS[item.month - 1] : "month";
      errors.push(`Day must be between 1 and ${maxDays} for ${monthName}`);
    }
  }

  const needsTime = item.precision === "exact_datetime" || item.precision === "approximate_datetime";
  if (needsTime) {
    if (!item.time || !isValidTimeHHmm(item.time)) {
      errors.push("Time must be in valid 24-hour HH:mm format (00:00–23:59)");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Compares two single dates chronologically.
 *
 * @param {ISingleDate | undefined} a - First date.
 * @param {ISingleDate | undefined} b - Second date.
 * @returns {number} Negative if a < b, 0 if equal, positive if a > b.
 */
export function compareSingleDates(a?: ISingleDate, b?: ISingleDate): number {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;

  if (a.year !== b.year) return a.year - b.year;

  const aMonth = a.month ?? 1;
  const bMonth = b.month ?? 1;
  if (aMonth !== bMonth) return aMonth - bMonth;

  const aDay = a.day ?? 1;
  const bDay = b.day ?? 1;
  if (aDay !== bDay) return aDay - bDay;

  const aTime = a.time ?? "00:00";
  const bTime = b.time ?? "00:00";
  return aTime.localeCompare(bTime);
}

/**
 * Parses a duration string into a structured IDateDuration.
 * Supports minutes ("minutes"/"mins"/"m"), hours ("hours"/"hr"/"hrs"/"h"),
 * days ("days"/"dy"/"dys"), weeks ("weeks"/"wk"/"wks"),
 * months ("months"/"mo"/"mos"), and years ("years"/"yr"/"yrs").
 *
 * @param {string | null | undefined} raw - Duration string to parse (e.g. "2 hours", "30 mins", "3 dys", "1 yr").
 * @returns {IDateDuration | null} Parsed duration or null if invalid.
 */
export function parseDuration(raw?: string | null): IDateDuration | null {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;

  const match = trimmed.match(/^(\d+(?:\.\d+)?)\s*([a-z]+)$/i);
  if (!match) return null;

  const num = parseFloat(match[1]);
  if (isNaN(num) || num <= 0) return null;

  const unitRaw = match[2].toLowerCase();
  let unit: DateDurationUnit | null = null;

  if (["m", "min", "mins", "minute", "minutes"].includes(unitRaw)) {
    unit = "minutes";
  } else if (["h", "hr", "hrs", "hour", "hours"].includes(unitRaw)) {
    unit = "hours";
  } else if (["d", "dy", "dys", "day", "days"].includes(unitRaw)) {
    unit = "days";
  } else if (["w", "wk", "wks", "week", "weeks"].includes(unitRaw)) {
    unit = "weeks";
  } else if (["mo", "mos", "month", "months"].includes(unitRaw)) {
    unit = "months";
  } else if (["y", "yr", "yrs", "year", "years"].includes(unitRaw)) {
    unit = "years";
  }

  if (!unit) return null;

  return {
    value: num,
    unit,
    raw: raw.trim(),
  };
}

/**
 * Formats a duration into standard human-readable display text.
 *
 * @param {IDateDuration | string} duration - Structured duration or duration string.
 * @returns {string} Formatted duration (e.g. "2 hours", "30 mins", "1 day").
 */
export function formatDuration(duration: IDateDuration | string): string {
  const parsed = typeof duration === "string" ? parseDuration(duration) : duration;
  if (!parsed) return typeof duration === "string" ? duration : "";
  const { value, unit } = parsed;

  const unitSingularMap: Record<DateDurationUnit, string> = {
    minutes: "min",
    hours: "hour",
    days: "day",
    weeks: "week",
    months: "month",
    years: "year",
  };
  const unitPluralMap: Record<DateDurationUnit, string> = {
    minutes: "mins",
    hours: "hours",
    days: "days",
    weeks: "weeks",
    months: "months",
    years: "years",
  };

  const label = value === 1 ? unitSingularMap[unit] : unitPluralMap[unit];
  return `${value} ${label}`;
}

/**
 * Calculates a target ISingleDate by adding a duration to a starting ISingleDate.
 *
 * @param {ISingleDate} from - Starting date item.
 * @param {string | IDateDuration} duration - Duration string or structured duration.
 * @returns {ISingleDate} Calculated end date item.
 */
export function calculateToDateFromDuration(
  from: ISingleDate,
  duration: string | IDateDuration
): ISingleDate {
  const parsedDur = typeof duration === "string" ? parseDuration(duration) : duration;
  if (!parsedDur) {
    return { ...from };
  }

  const { value, unit } = parsedDur;
  const isApprox = Boolean(from.isApproximate);
  const origYear = from.year || 2026;
  const origMonth = from.month ?? 1;
  const origDay = from.day ?? 1;

  let origHour = 0;
  let origMin = 0;
  if (from.time) {
    const parts = from.time.split(":");
    origHour = parseInt(parts[0] || "0", 10);
    origMin = parseInt(parts[1] || "0", 10);
  }

  const dt = new Date(Date.UTC(origYear, origMonth - 1, origDay, origHour, origMin, 0, 0));
  dt.setUTCFullYear(origYear);

  switch (unit) {
    case "minutes":
      dt.setUTCMinutes(dt.getUTCMinutes() + value);
      break;
    case "hours":
      dt.setUTCHours(dt.getUTCHours() + value);
      break;
    case "days":
      dt.setUTCDate(dt.getUTCDate() + value);
      break;
    case "weeks":
      dt.setUTCDate(dt.getUTCDate() + value * 7);
      break;
    case "months": {
      const totalMonths = (origMonth - 1) + value;
      let targetYear = origYear + Math.floor(totalMonths / 12);
      let targetMonth0 = totalMonths % 12;
      if (targetMonth0 < 0) {
        targetMonth0 += 12;
        targetYear -= 1;
      }
      const maxDaysInTarget = getDaysInMonth(targetYear, targetMonth0 + 1);
      const targetDay = Math.min(origDay, maxDaysInTarget);
      dt.setUTCDate(1);
      dt.setUTCFullYear(targetYear);
      dt.setUTCMonth(targetMonth0);
      dt.setUTCDate(targetDay);
      break;
    }
    case "years": {
      const targetYear = origYear + value;
      const maxDaysInTarget = getDaysInMonth(targetYear, origMonth);
      const targetDay = Math.min(origDay, maxDaysInTarget);
      dt.setUTCDate(1);
      dt.setUTCFullYear(targetYear);
      dt.setUTCMonth(origMonth - 1);
      dt.setUTCDate(targetDay);
      break;
    }
  }

  const resYear = dt.getUTCFullYear();
  const resMonth = dt.getUTCMonth() + 1;
  const resDay = dt.getUTCDate();
  const resHour = dt.getUTCHours();
  const resMin = dt.getUTCMinutes();
  const timeStr = `${String(resHour).padStart(2, "0")}:${String(resMin).padStart(2, "0")}`;

  let precision: DatePrecision = from.precision;
  let resTime: string | undefined = from.time;

  if (unit === "minutes" || unit === "hours") {
    precision = isApprox ? "approximate_datetime" : "exact_datetime";
    resTime = timeStr;
  } else if (unit === "days" || unit === "weeks") {
    if (from.precision === "exact_datetime" || from.precision === "approximate_datetime") {
      resTime = from.time || timeStr;
    } else {
      precision = isApprox ? "approximate_date" : "exact_date";
      resTime = undefined;
    }
  } else if (unit === "months") {
    if (from.precision === "year") {
      precision = "year_month";
      resTime = undefined;
    } else if (from.precision === "year_month") {
      precision = "year_month";
      resTime = undefined;
    } else if (from.precision === "exact_datetime" || from.precision === "approximate_datetime") {
      resTime = from.time || timeStr;
    } else {
      precision = isApprox ? "approximate_date" : "exact_date";
      resTime = undefined;
    }
  } else if (unit === "years") {
    if (from.precision === "year") {
      precision = "year";
      resTime = undefined;
    } else if (from.precision === "year_month") {
      precision = "year_month";
      resTime = undefined;
    } else if (from.precision === "exact_datetime" || from.precision === "approximate_datetime") {
      resTime = from.time || timeStr;
    } else {
      precision = isApprox ? "approximate_date" : "exact_date";
      resTime = undefined;
    }
  }

  const result: ISingleDate = {
    precision,
    year: resYear,
    month: precision === "year" ? undefined : resMonth,
    day: precision === "year" || precision === "year_month" ? undefined : resDay,
    time: resTime,
    isApproximate: isApprox,
    dateStr: "",
  };
  result.dateStr = formatSingleDate(result);
  return result;
}

/**
 * Validates an entire structured date payload (single or range).
 *
 * @param {IDate} payload - Structured date payload.
 * @returns {IDateValidationResult} Validation outcome with error messages.
 */
export function validateDate(payload: IDate): IDateValidationResult {
  const errors: string[] = [];
  const isRange = payload.type === "range" || (payload.type as any) === "Range";

  if (!isRange) {
    const fromItem = payload.from;
    const singleResult = validateSingleDate(fromItem);
    if (!singleResult.isValid) {
      errors.push(...singleResult.errors);
    }
  } else if (isRange) {
    const fromItem = payload.from;
    const fromResult = validateSingleDate(fromItem);
    if (!fromResult.isValid) {
      errors.push(...fromResult.errors.map((e) => `From date: ${e}`));
    }

    if (payload.duration) {
      const parsedDur = parseDuration(payload.duration);
      if (!parsedDur) {
        errors.push("Invalid duration format");
      } else if (!payload.to && fromResult.isValid && fromItem) {
        payload.to = calculateToDateFromDuration(fromItem, parsedDur);
      }
    }

    const toResult = validateSingleDate(payload.to);
    if (!toResult.isValid) {
      errors.push(...toResult.errors.map((e) => `To date: ${e}`));
    }

    if (fromResult.isValid && toResult.isValid) {
      if (compareSingleDates(fromItem, payload.to) > 0) {
        errors.push("Start date cannot be after end date");
      }
    }
  } else {
    errors.push("Invalid date mode");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Formats a single date into a human-readable display string.
 *
 * @param {ISingleDate} item - The single date item to format.
 * @returns {string} Human-readable representation.
 */
export function formatSingleDate(item: ISingleDate): string {
  const prefix = item.isApproximate ? "~ " : "";
  const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

  switch (item.precision) {
    case "exact_datetime":
    case "approximate_datetime": {
      const mName =
        item.month && item.month >= 1 && item.month <= 12
          ? MONTH_ABBREVIATIONS[item.month - 1]
          : "Jan";
      const d = item.day ? pad(item.day) : "01";
      const t = formatTimeHHmm(item.time);
      return `${prefix}${d}-${mName}-${item.year} ${t}`;
    }
    case "exact_date":
    case "approximate_date": {
      const mName =
        item.month && item.month >= 1 && item.month <= 12
          ? MONTH_ABBREVIATIONS[item.month - 1]
          : "Jan";
      const d = item.day ? pad(item.day) : "01";
      return `${prefix}${d}-${mName}-${item.year}`;
    }
    case "year_month": {
      const mName =
        item.month && item.month >= 1 && item.month <= 12
          ? MONTH_ABBREVIATIONS[item.month - 1]
          : "Unknown";
      return `${prefix}${mName} ${item.year}`;
    }
    case "year": {
      return `${prefix}${item.year}`;
    }
    default:
      return `${prefix}${item.dateStr || `${item.year}`}`;
  }
}

/**
 * Formats an entire structured date payload into a single coherent label string.
 *
 * @param {IDate} payload - Structured date payload.
 * @returns {string} Formatted label string.
 */
export function formatDate(payload: IDate): string {
  const isRange = payload.type === "range" || (payload.type as any) === "Range";
  const fromItem = payload.from;

  if (!isRange) {
    if (!fromItem) return "";
    return formatSingleDate(fromItem);
  }

  const fromStr = fromItem ? formatSingleDate(fromItem) : "?";
  const toStr = payload.to ? formatSingleDate(payload.to) : "?";
  if (payload.duration) {
    return `${fromStr} (${payload.duration})`;
  }
  return `${fromStr} to ${toStr}`;
}

export const formatDatePayload = formatDate;

/**
 * Formats a memory or media date value for display.
 * If the date is a JSON block (serialized string or object), returns the human-readable text, not the JSON.
 * If it is already plain text, returns the text directly.
 * If null, undefined, or empty, returns an empty string.
 *
 * @param {string | IDate | null | undefined} val - Raw date string or payload.
 * @returns {string} Human-readable date text.
 */
export function formatDisplayDate(val?: string | IDate | null): string {
  if (!val) return "";
  if (typeof val === "object") {
    if (typeof (val as any).formatted === "string" && (val as any).formatted.trim()) {
      return (val as any).formatted.trim();
    }
    return formatDate(val as IDate);
  }
  const str = String(val).trim();
  if (!str) return "";
  if (str.startsWith("{") && str.endsWith("}")) {
    try {
      const parsed = JSON.parse(str);
      if (parsed && typeof parsed === "object") {
        if (typeof parsed.formatted === "string" && parsed.formatted.trim()) {
          return parsed.formatted.trim();
        }
        if (parsed.from || parsed.single || parsed.type || parsed.mode) {
          const isRange = String(parsed.type || parsed.mode || "").toLowerCase() === "range";
          const type: DateType = isRange ? "range" : "date";
          const from = parsed.from || parsed.single;
          const payload: IDate = {
            ...parsed,
            type,
            from,
            formatted: "",
          };
          return formatDate(payload);
        }
      }
    } catch {
      // Not valid JSON, return original string
    }
  }
  return str;
}

/**
 * Checks whether a date field (payload object or serialized JSON) is marked as userDefined.
 * Returns true if explicitly user-defined (manually specified by user).
 * Returns false if default/imported/metadata-derived (e.g. EXIF capturedAt).
 *
 * @param {string | IDate | null | undefined} val - Date field value.
 * @returns {boolean} Whether the date was user-defined.
 */
export function isDateUserDefined(val?: string | IDate | null): boolean {
  if (!val) return false;
  if (typeof val === "object") {
    return Boolean((val as any).userDefined);
  }
  if (typeof val === "string") {
    const str = val.trim();
    if (str.startsWith("{") && str.endsWith("}")) {
      try {
        const parsed = JSON.parse(str);
        return Boolean(parsed?.userDefined);
      } catch {
        return false;
      }
    }
  }
  return false;
}

/**
 * Creates a single date item.
 *
 * @param {DatePrecision} precision - Date precision level.
 * @param {number} year - Four-digit year.
 * @param {number} [month] - 1-indexed month (1-12).
 * @param {number} [day] - Day of month (1-31).
 * @param {string} [time] - Time string (HH:mm).
 * @param {boolean} [isApproximate] - Explicit approximate override.
 * @returns {ISingleDate} Created single date object.
 */
export function createSingleDate(
  precision: DatePrecision,
  year: number,
  month?: number,
  day?: number,
  time?: string,
  isApproximate?: boolean
): ISingleDate {
  const approx = isApproximate ?? (precision === "approximate_date" || precision === "approximate_datetime");
  const normalizedTime = time ? formatTimeHHmm(time) : undefined;
  let sanitizedDay = day;
  if (sanitizedDay !== undefined && month !== undefined && month >= 1 && month <= 12) {
    const maxDays = getDaysInMonth(year, month);
    if (sanitizedDay > maxDays) sanitizedDay = maxDays;
    if (sanitizedDay < 1) sanitizedDay = 1;
  }
  const temp: ISingleDate = {
    precision,
    isApproximate: approx,
    year,
    month,
    day: sanitizedDay,
    time: normalizedTime,
    dateStr: "",
  };
  temp.dateStr = formatSingleDate(temp);
  return temp;
}

/**
 * Serializes a date payload to a JSON string for database storage.
 *
 * @param {IDate} payload - Payload to serialize.
 * @returns {string} Stringified JSON representation.
 */
export function serializeDate(payload: IDate): string {
  const isRange = String(payload.type || "").toLowerCase() === "range";
  const type: DateType = isRange ? "range" : "date";
  const from = payload.from || createSingleDate("exact_date", new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate());
  const updated: IDate = {
    type,
    from,
    ...(type === "range" && payload.to ? { to: payload.to } : {}),
    formatted: formatDate({ ...payload, type, from }),
  };
  return JSON.stringify(updated);
}

/**
 * Result structure returned by typed date validator.
 */
export interface ITypedDateValidationResult extends IDateValidationResult {
  parsed?: IDate;
}

/**
 * Validates a user-typed date string against standard Date formats.
 * Checks whether the input represents a valid exact/approximate date, datetime,
 * month-year, year, or range, verifying that day, month, year, and time ranges are physically valid.
 *
 * @param {string | undefined | null} input - Raw user input string.
 * @returns {ITypedDateValidationResult} Validation outcome with error messages and parsed payload.
 */
export function validateTypedDate(input?: string | null): ITypedDateValidationResult {
  if (!input || !input.trim()) {
    return {
      isValid: false,
      errors: ["Date cannot be empty"],
    };
  }

  const trimmed = input.trim();

  // 1. JSON payload validation
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      const parsed = JSON.parse(trimmed) as Record<string, any>;
      if (parsed && (parsed.type || parsed.mode || parsed.from || parsed.single)) {
        const rawType = parsed.type || parsed.mode;
        const isRange = String(rawType || "").toLowerCase() === "range";
        const type: DateType = isRange ? "range" : "date";
        const from = parsed.from || parsed.single;
        const normalizedParsed: IDate = {
          ...parsed,
          type,
          from,
          formatted: "",
        };
        normalizedParsed.formatted = formatDate(normalizedParsed);
        const payloadValidation = validateDate(normalizedParsed);
        if (payloadValidation.isValid) {
          return {
            isValid: true,
            errors: [],
            parsed: normalizedParsed,
          };
        }
        return {
          isValid: false,
          errors: payloadValidation.errors,
        };
      }
    } catch {
      return {
        isValid: false,
        errors: ["Invalid JSON date string format"],
      };
    }
  }

  // 2. Date with duration expression (e.g. "10-Oct-2026 for 2 hours", "10-Oct-2026 + 3 days", "10-Oct-2026 (2 hrs)")
  const durationMatch = trimmed.match(
    /^(.*?)(?:[\s,]+(?:for|duration:?|\+|\(|-)?\s*|\s*\()\s*(\d+(?:\.\d+)?)\s*(minutes|minute|mins|min|m|hours|hour|hrs|hr|h|days|day|dys|dy|d|weeks|week|wks|wk|w|months|month|mos|mo|years|year|yrs|yr|y)\)?$/i
  );
  if (durationMatch) {
    const datePart = durationMatch[1].trim();
    const durRaw = `${durationMatch[2]} ${durationMatch[3]}`;
    const parsedDur = parseDuration(durRaw);
    if (parsedDur && datePart) {
      const fromRes = validateTypedDate(datePart);
      if (!fromRes.isValid) {
        return { isValid: false, errors: fromRes.errors.map((e) => `Start date: ${e}`) };
      }
      const fromSingle = fromRes.parsed?.from;
      if (fromSingle) {
        const canonicalDur = formatDuration(parsedDur);
        const toSingle = calculateToDateFromDuration(fromSingle, parsedDur);
        const rangePayload: IDate = {
          type: "range",
          from: fromSingle,
          to: toSingle,
          duration: canonicalDur,
          formatted: "",
        };
        rangePayload.formatted = formatDate(rangePayload);
        return {
          isValid: true,
          errors: [],
          parsed: rangePayload,
        };
      }
    }
  }

  // 3. Date Range validation: "fromStr to toStr" or "fromStr - toStr"
  const rangeSplitRegex = /\s+(?:to|-|–|—)\s+/i;
  if (rangeSplitRegex.test(trimmed)) {
    const parts = trimmed.split(rangeSplitRegex);
    const fromStr = parts[0]?.trim();
    const toStr = parts.slice(1).join(" to ").trim();
    const fromRes = validateTypedDate(fromStr);
    const toRes = validateTypedDate(toStr);

    const errors: string[] = [];
    if (!fromRes.isValid) {
      errors.push(...fromRes.errors.map((e) => `Start date: ${e}`));
    }
    if (!toRes.isValid) {
      errors.push(...toRes.errors.map((e) => `End date: ${e}`));
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    const fromSingle = fromRes.parsed?.from;
    const toSingle = toRes.parsed?.from;

    if (compareSingleDates(fromSingle, toSingle) > 0) {
      return {
        isValid: false,
        errors: ["Start date cannot be after end date"],
      };
    }

    const rangePayload: IDate = {
      type: "range",
      from: fromSingle,
      to: toSingle,
      formatted: "",
    };
    rangePayload.formatted = formatDate(rangePayload);

    return {
      isValid: true,
      errors: [],
      parsed: rangePayload,
    };
  }

  // 3. Single date parsing and component validation
  const isApproximate =
    trimmed.startsWith("~") ||
    trimmed.toLowerCase().startsWith("about ") ||
    trimmed.toLowerCase().startsWith("c. ") ||
    trimmed.toLowerCase().startsWith("c ");
  const cleanStr = trimmed
    .replace(/^~\s*/, "")
    .replace(/^about\s+/i, "")
    .replace(/^c\.\s*/i, "")
    .replace(/^c\s+/i, "")
    .trim();

  const resolveMonthNum = (val: string): { num: number; valid: boolean } => {
    const raw = val.toLowerCase();
    const abbrevIdx = MONTH_ABBREVIATIONS.findIndex((m) => m.toLowerCase() === raw);
    if (abbrevIdx >= 0) return { num: abbrevIdx + 1, valid: true };
    const fullIdx = MONTH_NAMES.findIndex((m) => m.toLowerCase() === raw);
    if (fullIdx >= 0) return { num: fullIdx + 1, valid: true };
    const num = parseInt(val, 10);
    if (!isNaN(num) && num >= 1 && num <= 12) return { num, valid: true };
    return { num: 1, valid: false };
  };

  // Pattern A: "dd-MMM-yyyy HH:mm[:ss]" or "dd-MMM-yyyy"
  const ddMmmYyyyMatch = cleanStr.match(/^(\d{1,2})-([A-Za-z]{3,})-(\d{4})(?:\s+(\d{1,2}:\d{2}(?::\d{2})?))?$/);
  if (ddMmmYyyyMatch) {
    const day = parseInt(ddMmmYyyyMatch[1], 10);
    const monthInfo = resolveMonthNum(ddMmmYyyyMatch[2]);
    const year = parseInt(ddMmmYyyyMatch[3], 10);
    const timeStr = ddMmmYyyyMatch[4];

    const errors: string[] = [];
    if (!monthInfo.valid) {
      errors.push(`Invalid month: "${ddMmmYyyyMatch[2]}"`);
    }
    if (year < 1 || year > 9999) {
      errors.push("Year must be between 1 and 9999");
    }
    const maxDays = getDaysInMonth(year, monthInfo.num);
    if (day < 1 || day > maxDays) {
      errors.push(`Day must be between 1 and ${maxDays} for ${ddMmmYyyyMatch[2]}`);
    }
    if (timeStr && !isValidTimeHHmm(timeStr)) {
      errors.push("Time must be in valid 24-hour HH:mm format (00:00–23:59)");
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    const precision: DatePrecision = timeStr
      ? isApproximate
        ? "approximate_datetime"
        : "exact_datetime"
      : isApproximate
      ? "approximate_date"
      : "exact_date";

    const single = createSingleDate(precision, year, monthInfo.num, day, timeStr, isApproximate);
    return {
      isValid: true,
      errors: [],
      parsed: {
        type: "date",
        from: single,
        formatted: formatSingleDate(single),
      },
    };
  }

  // Pattern B: "YYYY-MM-DD HH:mm[:ss]" or "YYYY-MM-DD"
  const isoDateMatch = cleanStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+(\d{1,2}:\d{2}(?::\d{2})?))?$/);
  if (isoDateMatch) {
    const year = parseInt(isoDateMatch[1], 10);
    const month = parseInt(isoDateMatch[2], 10);
    const day = parseInt(isoDateMatch[3], 10);
    const timeStr = isoDateMatch[4];

    const errors: string[] = [];
    if (year < 1 || year > 9999) {
      errors.push("Year must be between 1 and 9999");
    }
    if (month < 1 || month > 12) {
      errors.push("Month must be between 1 and 12");
    }
    const maxDays = getDaysInMonth(year, month);
    if (day < 1 || day > maxDays) {
      errors.push(`Day must be between 1 and ${maxDays}`);
    }
    if (timeStr && !isValidTimeHHmm(timeStr)) {
      errors.push("Time must be in valid 24-hour HH:mm format (00:00–23:59)");
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    const precision: DatePrecision = timeStr
      ? isApproximate
        ? "approximate_datetime"
        : "exact_datetime"
      : isApproximate
      ? "approximate_date"
      : "exact_date";

    const single = createSingleDate(precision, year, month, day, timeStr, isApproximate);
    return {
      isValid: true,
      errors: [],
      parsed: {
        type: "date",
        from: single,
        formatted: formatSingleDate(single),
      },
    };
  }

  // Pattern C: "Month YYYY" (e.g. "October 2026" or "Oct 2026")
  const monthYearMatch = cleanStr.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (monthYearMatch) {
    const monthInfo = resolveMonthNum(monthYearMatch[1]);
    const year = parseInt(monthYearMatch[2], 10);

    const errors: string[] = [];
    if (!monthInfo.valid) {
      errors.push(`Invalid month: "${monthYearMatch[1]}"`);
    }
    if (year < 1 || year > 9999) {
      errors.push("Year must be between 1 and 9999");
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    const single = createSingleDate("year_month", year, monthInfo.num, undefined, undefined, isApproximate);
    return {
      isValid: true,
      errors: [],
      parsed: {
        type: "date",
        from: single,
        formatted: formatSingleDate(single),
      },
    };
  }

  // Pattern D: "YYYY-MM"
  const ymDashMatch = cleanStr.match(/^(\d{4})-(\d{1,2})$/);
  if (ymDashMatch) {
    const year = parseInt(ymDashMatch[1], 10);
    const month = parseInt(ymDashMatch[2], 10);

    const errors: string[] = [];
    if (year < 1 || year > 9999) {
      errors.push("Year must be between 1 and 9999");
    }
    if (month < 1 || month > 12) {
      errors.push("Month must be between 1 and 12");
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    const single = createSingleDate("year_month", year, month, undefined, undefined, isApproximate);
    return {
      isValid: true,
      errors: [],
      parsed: {
        type: "date",
        from: single,
        formatted: formatSingleDate(single),
      },
    };
  }

  // Pattern E: "YYYY" (e.g. "2026")
  const yearMatch = cleanStr.match(/^(\d{4})$/);
  if (yearMatch) {
    const year = parseInt(yearMatch[1], 10);
    if (year < 1 || year > 9999) {
      return {
        isValid: false,
        errors: ["Year must be between 1 and 9999"],
      };
    }

    const single = createSingleDate("year", year, undefined, undefined, undefined, isApproximate);
    return {
      isValid: true,
      errors: [],
      parsed: {
        type: "date",
        from: single,
        formatted: formatSingleDate(single),
      },
    };
  }

  // Pattern F: Fallback standard JS Date.parse check
  const parsedTs = Date.parse(cleanStr);
  if (!isNaN(parsedTs)) {
    const d = new Date(parsedTs);
    const year = d.getFullYear();
    if (year >= 1 && year <= 9999) {
      const single = createSingleDate(
        isApproximate ? "approximate_date" : "exact_date",
        year,
        d.getMonth() + 1,
        d.getDate(),
        undefined,
        isApproximate
      );
      return {
        isValid: true,
        errors: [],
        parsed: {
          type: "date",
          from: single,
          formatted: formatSingleDate(single),
        },
      };
    }
  }

  return {
    isValid: false,
    errors: ["Invalid date format (e.g. 09-Oct-2026, 2026-10-09, Oct 2026, 2026)"],
  };
}

export const DateValidation = {
  validateSingle: validateSingleDate,
  validatePayload: validateDate,
  validateTypedDate,
  isValidTime: isValidTimeHHmm,
  getDaysInMonth,
  isLeapYear,
  compareSingleDates,
};

/**
 * Formats a Unix epoch timestamp (milliseconds or seconds) or Date into the canonical card timestamp:
 * format: dd-MMM-yyyy HH:mm:ss (e.g. "25-Oct-2026 14:30:15").
 *
 * @param {number | string | Date} [val] - The timestamp or date value to format.
 * @returns {string} The formatted timestamp string.
 */
export function formatCardTimestamp(val?: number | string | Date | null): string {
  if (!val) return "";
  const d =
    typeof val === "number"
      ? new Date(val > 1e11 ? val : val * 1000)
      : val instanceof Date
      ? val
      : new Date(val);

  if (isNaN(d.getTime())) return "";

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = String(d.getDate()).padStart(2, "0");
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
}

